/** Three small pieces of the Targets & plan place (v5, stage F5), each one an honest statement of
 * what the plan can and cannot do.
 *
 * - **`InitiativeBars`** draws expected beside achieved, one pair per initiative, **never added**
 *   (EXP-37: a projection and a measurement in one column is a plan that reads as a result). Expected
 *   is a dashed outline (modelled); achieved is solid (recorded, with its evidence count). Both bars
 *   share one scale so their lengths compare, and the chart is drawn only when every figure is in
 *   one measure, because bars of tonnes beside bars of anything else on one axis say nothing.
 * - **`ApplyAsPlan`** is a control that is *disabled with its reason*, because the backend has no
 *   write that turns a scenario into initiatives, and a scenario is a recipe and never a figure
 *   (ADR-0193). A button that predictably failed would be worse than saying so.
 * - **`CostLane`** says no cost per tonne is drawn and why: cost needs a source for every input and
 *   a place in the model, and neither exists (ADR-0193, D-4). No number is invented to fill it.
 */

import { Link } from "@tanstack/react-router";

import type { PlanInitiative } from "../api/queries";
import { ChartTable } from "../components/charts/ChartTable";
import { factorValue } from "../lib/format";
import { RecordLink } from "./RecordLink";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import own from "./TransitionPlanExtras.module.css";

interface Pair {
  initiative: PlanInitiative;
  expected: number | null;
  achieved: number | null;
}

/** The bars' data, or the reason there are none. Pure, so a test constructs each condition. */
export function initiativeBarsModel(
  initiatives: readonly PlanInitiative[],
): { kind: "none" } | { kind: "mixed_measures" } | { kind: "bars"; unit: string; pairs: Pair[]; longest: number } {
  const withFigures = initiatives.filter((item) => item.expected !== null || item.achieved !== null);
  if (withFigures.length === 0) return { kind: "none" };
  const measures = new Set(
    withFigures.flatMap((item) => [
      ...(item.expected !== null ? [item.expected_unit ?? ""] : []),
      ...(item.achieved !== null ? [item.achieved_unit ?? ""] : []),
    ]),
  );
  const [unit] = [...measures];
  if (measures.size !== 1 || unit === undefined || unit === "") return { kind: "mixed_measures" };
  const pairs = withFigures.map((item) => ({
    initiative: item,
    expected: item.expected !== null ? Number(item.expected) : null,
    achieved: item.achieved !== null ? Number(item.achieved) : null,
  }));
  if (pairs.some((pair) => [pair.expected, pair.achieved].some((v) => v !== null && (!Number.isFinite(v) || v < 0)))) {
    return { kind: "mixed_measures" };
  }
  const longest = Math.max(0, ...pairs.flatMap((pair) => [pair.expected ?? 0, pair.achieved ?? 0]));
  return { kind: "bars", unit, pairs, longest };
}

export function InitiativeBars({
  initiatives,
  search,
}: {
  initiatives: readonly PlanInitiative[];
  search: Partial<ScreenSearch>;
}) {
  const model = initiativeBarsModel(initiatives);
  if (model.kind === "none") return null;
  if (model.kind === "mixed_measures") {
    return (
      <p className={own.note} data-testid="initiative-bars-not-drawn">
        The initiatives are stated in different measures, so they are listed and not charted: bars
        of different measures on one scale would compare nothing.
      </p>
    );
  }
  const carry = keepSearch(search);
  return (
    <div data-testid="initiative-bars">
      {/* The one list of initiatives when the bars are drawn (F6: the cards that used to follow
          repeated every name, figure and link). Each row keeps what the card carried: the pair of
          figures with "modelled" said, the evidence count, and the way to the record. */}
      <ul className={own.list} data-testid="initiatives">
        {model.pairs.map(({ initiative, expected, achieved }) => (
          <li key={initiative.id} data-initiative={initiative.id} className={own.item}>
            <Link
              to="/records/$recordId"
              params={{ recordId: initiative.id }}
              search={carry}
              className={own.pair}
              aria-label={`${initiative.name}. Expected ${expected === null ? "not estimated" : `${factorValue(initiative.expected)} ${model.unit}, modelled`}. Achieved ${achieved === null ? "not yet" : `${factorValue(initiative.achieved)} ${model.unit}`}. Open the initiative.`}
            >
              <span className={own.name}>{initiative.name}</span>
              <span className={own.bars} aria-hidden="true">
                <span className={own.track}>
                  {expected !== null ? (
                    <span
                      className={own.expected}
                      style={{ width: `${model.longest > 0 ? (expected / model.longest) * 100 : 0}%` }}
                    />
                  ) : null}
                </span>
                <span className={own.track}>
                  {achieved !== null ? (
                    <span
                      className={own.achieved}
                      style={{ width: `${model.longest > 0 ? (achieved / model.longest) * 100 : 0}%` }}
                    />
                  ) : null}
                </span>
              </span>
              <span className={`${own.figures} num`}>
                <span>
                  {expected === null
                    ? "Expected: not estimated"
                    : `Expected ${factorValue(initiative.expected)}, modelled`}
                </span>
                <span>
                  {achieved === null ? "Achieved: not yet" : `Achieved ${factorValue(initiative.achieved)}`}
                  {", "}
                  {initiative.evidence_document_ids.length > 0
                    ? `${initiative.evidence_document_ids.length} document${initiative.evidence_document_ids.length === 1 ? "" : "s"}`
                    : "no evidence yet"}
                </span>
              </span>
            </Link>
            {/* An initiative is a record (`INITIATIVE`): "is this saving double-counted?" is a flag
                and "who signed off this 180 tCO2e?" is an approval (F-007). */}
            <RecordLink recordId={initiative.id} kind="initiative" subject={initiative.name} />
          </li>
        ))}
      </ul>
      <p className={own.key}>
        <span className={own.keyExpected} aria-hidden="true" /> Expected, modelled
        <span className={own.keyAchieved} aria-hidden="true" /> Achieved, with evidence. {model.unit}. The two are
        never added.
      </p>
      <ChartTable
        caption={`Initiatives, expected and achieved, ${model.unit}`}
        head={["Initiative", "Expected (modelled)", "Achieved"]}
        rows={model.pairs.map(({ initiative }) => [
          initiative.name,
          initiative.expected === null ? "not estimated" : factorValue(initiative.expected),
          initiative.achieved === null ? "not yet" : factorValue(initiative.achieved),
        ])}
      />
    </div>
  );
}

export function ApplyAsPlan() {
  return (
    <div className={own.apply} data-testid="apply-as-plan">
      <button type="button" disabled aria-describedby="apply-as-plan-why" className={own.disabled}>
        Apply as plan
      </button>
      <span id="apply-as-plan-why" className={own.note}>
        Not available yet. A scenario is a recipe and never a figure, and nothing turns one into delivery
        actions yet. Add an action under <strong>What we are doing</strong> to make it part of the plan.
      </span>
    </div>
  );
}

export function CostLane() {
  return (
    <p className={own.note} data-testid="cost-lane">
      <strong>No cost per tonne is drawn.</strong> A cost needs a stated, dated source for every
      input and a place in the model, and the plan holds neither: it records tonnes and no money. A
      ratio built on invented prices would read as a figure, so this stays empty until the inputs
      exist.
    </p>
  );
}
